<template>
    <div class="kpmi-container">
        <div v-for="(item, index) in items" :key="index">
            <MultiItemItem
                :value="item"
                :single="single"
                :text-name="textName"
                :type-name="typeName"
                :item-name="itemName"
                :show-text-field="showTextField"
                :show-type-field="showTypeField"
                :show-item-field="showItemField"
                :index="index"
                :container-class="containerClass"
                :action-class="actionClass"
                :text-class="textClass"
                :type-class="typeClass"
                :item-class="itemClass"
                @add-item="addItem"
                @remove-item="removeItem"
                @input="changeItem"
            >
                <template #text="slotProps">
                    <slot name="text" v-bind="slotProps" />
                </template>
                <template #type="slotProps">
                    <slot name="type" v-bind="slotProps" />
                </template>
                <template #default="slotProps">
                    <slot v-bind="slotProps" />
                </template>
            </MultiItemItem>
            <v-divider v-if="index !== items.length - 1" class="mx-4" />
        </div>
    </div>
</template>

<script>
import MultiItemItem from '@/components/multi_item/Item';

/**
 * A component with multiple sub components with each up to three input fields.
 * Displays data from an array (value) in each sub component in the input fields and stores any changes made.
 *
 * Fires the following event:
 * - change: `input`: (value)
 *
 * There are three slots to customize the three fields:
 * - text
 * - default
 * - type
 * All slots receive the value and a input change function as slot properties.
 */
export default {
    name: 'MultiItemContainer',
    components: {
        MultiItemItem,
    },
    props: {
        /**
         * Array with objects to display.
         */
        value: {
            type: Array,
            default: null,
        },
        /**
         * Boolean indicating if container should be limited to 1 item.
         */
        single: {
            type: Boolean,
            default: false,
        },
        /**
         * Name of the text field (1st field). This is used as the property in the value object.
         */
        textName: {
            type: String,
            default: 'text',
        },
        /**
         * Name of the type field (2rd field). This is used as the property in the value object.
         */
        typeName: {
            type: String,
            default: 'type',
        },
        /**
         * Name of the item field (3nd field). This is used as the property in the value object.
         */
        itemName: {
            type: String,
            default: 'item',
        },
        /**
         * Show the text field (1st field).
         */
        showTextField: {
            type: Boolean,
            default: true,
        },
        /**
         * Show the type field (2rd field).
         */
        showTypeField: {
            type: Boolean,
            default: true,
        },
        /**
         * Show the item field (3nd field).
         */
        showItemField: {
            type: Boolean,
            default: true,
        },
        /** Additional class string to be set on the field container div */
        containerClass: {
            type: String,
            default: '',
        },
        /** Additional class string to be set on the text slot div (1st field) */
        textClass: {
            type: String,
            default: '',
        },
        /** Additional class string to be set on the type slot div (2rd field) */
        typeClass: {
            type: String,
            default: '',
        },
        /** Additional class string to be set on the item slot div (3nd field) */
        itemClass: {
            type: String,
            default: '',
        },
        /** Additional class string to be set on the action container div */
        actionClass: {
            type: String,
            default: '',
        },
    },
    computed: {
        items() {
            if (!this.value) {
                return [{}];
            }
            return this.value;
        },
    },
    methods: {
        addItem(index) {
            if (this.single) {
                return;
            }
            const val = this.items;
            val.splice(index + 1, 0, {});
            this.updateValue(val);
        },
        removeItem(index) {
            if (this.single) {
                return;
            }
            const val = this.items;
            val.splice(index, 1);
            if (val.length === 0) {
                val.push({});
            }
            this.updateValue(val);
        },
        changeItem(index, value) {
            const val = this.items;
            val[index] = value;
            this.updateValue(val);
        },
        updateValue(value) {
            // emit a new array, re-using the same reference causes trouble
            this.$emit('input', [...(value || [])]);
        },
    },
};
</script>

<style lang="scss" scoped>
.kpmi-container {
    background-color: var(--toolbar-bg-color);
    border-color: #e0e0e0;
    border-style: solid;
    border-width: 1px;
}
</style>
